/**
 * BM-H3775 (Vanessa, 6 Oct 2026): "compliance and pipeline actions really
 * means the same thing".
 *
 * The Today panel had a Compliance group (GET /compliance/my-tasks) and a
 * Pipeline Actions group (/mate/today-queue) that listed the same matters
 * twice: BL.SM.007.1390 was "Complete the FORF" in one and "AML / Thirdfort
 * Check" in the other. This folds both, plus the awaiting-conflict-check
 * Next Steps, into one list with one row per matter, most urgent first, and
 * a chip for each outstanding step. Tasks with no matter (the person's AML
 * training, the month's file review sampling) go in a separate strip.
 *
 * Vanessa, 6 Oct 2026: the Today panel "is meant to be a 'what do i need to
 * do today/right now' nudge". So the list holds only steps someone here can
 * act on now. A step waiting on someone else (the client, Thirdfort) is left
 * out of the rows; a matter with nothing but waiting steps is counted in
 * `waiting` instead, and the pipeline below the panel shows it.
 *
 * Every row and chip keeps its own way in: a pipeline step opens through
 * openPipelineAction (BM-H3734), a compliance task through its own in-app
 * link, an awaiting-conflict-check submission through its submission page.
 * Pure functions only, so the grouping is unit tested without a renderer.
 */
import { complianceTaskLink, complianceTaskSeverity } from "./todayCompliance";

// Lower is more urgent. Overdue and stopped come before anything that can
// simply be done next, and waiting on someone else comes last.
export const URGENCY = { overdue: 0, stopped: 1, next: 2, waiting: 3 };

// The pipeline stage labels mate.py puts on each action (_STAGE_LABELS).
const STAGE_CHIP = {
  conflict: "Conflict check",
  ob_sent: "Onboarding",
  ob_returned: "AML",
  forf_draft: "FORF",
  ccl_draft: "CCL",
};

// What a compliance task is called on its chip.
const KIND_CHIP = {
  complete_forf: "FORF",
  close_file_after_final_invoice: "Close file",
  mlco_decision: "MLCO",
  mlco_no_fee_earner: "MLCO",
  mlco_fee_earner_step: "MLCO",
  mlco_remediation: "MLCO",
  corrective_action: "Corrective action",
  file_review_sampled: "File review",
  file_review_draft: "File review",
  sampled_review_unassigned: "File review",
  complaint_acknowledge: "Complaint",
  complaint_update: "Complaint",
  complaint_respond: "Complaint",
  complaint_close: "Complaint",
  training_due: "Training",
  sampling_not_run: "File review",
};

function refKey(ref) {
  return String(ref || "").trim().toUpperCase();
}

function escapeRe(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** A task title with its own matter reference taken out, since the row shows it. */
export function withoutRef(title, ref) {
  let t = String(title || "");
  const r = String(ref || "").trim();
  if (r) {
    t = t.replace(new RegExp(`\\s+(?:for|on|of)\\s+${escapeRe(r)}`, "i"), "");
    t = t.replace(new RegExp(escapeRe(r), "gi"), "").replace(/\s{2,}/g, " ");
  }
  return t.replace(/\s+:/g, ":").trim();
}

function pipelineUrgency(item) {
  const state = item?.next_step?.state;
  if (state === "stopped") return URGENCY.stopped;
  if (state === "waiting") return URGENCY.waiting;
  if (state === "next") return URGENCY.next;
  // No next_step read: the conflict check is the one that blocks everything.
  return item?.stage === "conflict" ? URGENCY.stopped : URGENCY.next;
}

function pipelineSentence(item) {
  const ns = item?.next_step;
  if (ns && ns.title) {
    const lead = ns.state === "waiting" ? "Waiting: " : ns.state === "stopped" ? "Stopped: " : "";
    return lead + ns.title;
  }
  return item?.step || "";
}

function complianceUrgency(task) {
  if (task.overdue) return URGENCY.overdue;
  const sev = complianceTaskSeverity(task);
  return sev === "critical" || sev === "high" ? URGENCY.stopped : URGENCY.next;
}

function complianceStep(task) {
  const sentence = withoutRef(task.title, task.matter_ref);
  const due = task.overdue && task.due ? `overdue, was due ${task.due}` : task.due ? `due ${task.due}` : "";
  return {
    source: "compliance",
    label: KIND_CHIP[task.kind] || "Compliance",
    sentence: due && !sentence.includes(task.due) ? `${sentence} (${due})` : sentence,
    urgency: complianceUrgency(task),
    overdue: !!task.overdue,
    open: { kind: "link", link: complianceTaskLink(task) },
  };
}

/**
 * Fold the three sources into { matters, personal }.
 *
 * matters: one entry per matter with something to do now, each
 *   { ref, client, urgency, steps: [{ source, label, sentence, urgency, open }] }
 * with steps most urgent first and at most one step per chip label (the
 * pipeline's FORF step and the compliance "complete the FORF" task are the
 * same step). The first step is what clicking the row opens.
 *
 * personal: compliance tasks with no matter, most urgent first.
 *
 * waiting: matters whose only steps are waiting on someone else.
 */
export function buildTodayActions({ pipelineItems = [], complianceTasks = [], awaitingConflict = [] } = {}) {
  const groups = new Map();
  const order = [];
  const groupFor = (ref, client) => {
    const key = refKey(ref);
    if (!groups.has(key)) {
      groups.set(key, { ref: String(ref || "").trim(), client: client || "", steps: [], firstSeen: order.length });
      order.push(key);
    }
    const g = groups.get(key);
    if (!g.client && client) g.client = client;
    return g;
  };

  pipelineItems.forEach((item, i) => {
    const ref = item?.matter_reference || "";
    // A row with no reference at all (a provisional conflict check) still
    // needs a row of its own; key it by its conflict check id.
    const g = ref ? groupFor(ref, item.client_name) : groupFor(`cc:${item?.conflict_check_id || i}`, item?.client_name);
    if (!ref) g.ref = "";
    g.steps.push({
      source: "pipeline",
      label: STAGE_CHIP[item?.stage] || item?.step || "Pipeline",
      sentence: pipelineSentence(item),
      urgency: pipelineUrgency(item),
      priority: typeof item?.priority === "number" ? item.priority : 9,
      open: { kind: "pipeline", item },
    });
  });

  awaitingConflict.forEach((ns, i) => {
    const ref = ns?.matter_reference || "";
    const g = groupFor(ref || `sub:${ns?.id || i}`, ns?.client_name);
    if (!ref) g.ref = "";
    g.steps.push({
      source: "submission",
      label: "Conflict check",
      sentence: ns?.step || "Awaiting Conflict Check",
      urgency: URGENCY.stopped,
      priority: 0,
      open: { kind: "submission", id: ns?.id || null },
    });
  });

  const personal = [];
  complianceTasks.forEach((task) => {
    if (!task || !task.title) return;
    if (!String(task.matter_ref || "").trim()) {
      personal.push({ ...complianceStep(task), title: task.title });
      return;
    }
    groupFor(task.matter_ref, "").steps.push({ ...complianceStep(task), priority: 9 });
  });

  const waiting = [];
  const matters = order.map((key) => {
    const g = groups.get(key);
    const now = g.steps.filter((s) => s.urgency < URGENCY.waiting);
    if (!now.length) {
      waiting.push({ ref: g.ref, client: g.client });
      return null;
    }
    const sorted = [...now].sort((a, b) =>
      a.urgency - b.urgency || (a.priority ?? 9) - (b.priority ?? 9));
    const seen = new Set();
    const steps = [];
    for (const s of sorted) {
      if (seen.has(s.label)) continue;
      seen.add(s.label);
      steps.push(s);
    }
    return {
      ref: g.ref,
      client: g.client,
      urgency: steps.length ? steps[0].urgency : URGENCY.next,
      priority: Math.min(...steps.map((s) => s.priority ?? 9)),
      firstSeen: g.firstSeen,
      steps,
    };
  }).filter(Boolean);
  matters.sort((a, b) => a.urgency - b.urgency || a.priority - b.priority || a.firstSeen - b.firstSeen);
  personal.sort((a, b) => a.urgency - b.urgency);
  return { matters, personal, waiting, stepCount: matters.reduce((n, m) => n + m.steps.length, 0) };
}
